import { useCallback, useEffect, useState } from "react";

import clientSide from "@/helpers/clientSide";

/**
 * Observes the attached element's height and writes it (in pixels) to a CSS
 * custom property on `document.documentElement`, keeping it in sync with
 * layout changes via ResizeObserver.
 *
 * Useful for coordinating stacked `position: sticky` elements that live in
 * separate DOM branches, e.g. pinning a sticky banner directly below a
 * sticky navigation whose height is responsive.
 *
 * Returns a callback ref. We use a callback ref (rather than `useRef`) so
 * that the effect re-runs whenever the underlying element actually mounts
 * or unmounts — e.g. when the consumer conditionally returns `null` on the
 * first render while data is loading and mounts the element on a later
 * render.
 */
export default function useElementHeightCssVar<
  T extends HTMLElement = HTMLDivElement,
>(cssVarName: string) {
  const [element, setElement] = useState<T | null>(null);

  const ref = useCallback((node: T | null) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (!element || !clientSide) return;

    const root = document.documentElement;

    const update = () => {
      const { height } = element.getBoundingClientRect();
      root.style.setProperty(cssVarName, `${height}px`);
    };

    update();

    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(update);
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
      root.style.removeProperty(cssVarName);
    };
  }, [cssVarName, element]);

  return ref;
}
